import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import type { BulkDownloadManifestResponse } from '@ooh/contracts';

// ─── Constants ────────────────────────────────────────────────

const TWO_GB_BYTES = 2 * 1024 ** 3;
const DIALOG_MAX_WIDTH = 'max-w-lg';

// ─── Helpers ─────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${Math.round(bytes / 1_048_576)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

// ─── Types ────────────────────────────────────────────────────

interface BulkDownloadConfirmDialogProps {
  manifest: BulkDownloadManifestResponse;
  totalSelectedUnits: number;
  onConfirm: () => void;
  onCancel: () => void;
}

// ─── Component ───────────────────────────────────────────────

export function BulkDownloadConfirmDialog({
  manifest,
  totalSelectedUnits,
  onConfirm,
  onCancel,
}: BulkDownloadConfirmDialogProps) {
  const { t } = useTranslation('inventory');

  const isLarge = manifest.totalBytes > TWO_GB_BYTES;
  const skippedCount = manifest.unitsWithoutAttachments.length;
  const hasSkipped = skippedCount > 0;

  const header = (
    <div>
      <h2 className="text-lg font-semibold">{t('bulkDownload.dialog.title')}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('bulkDownload.dialog.fileCount', { count: manifest.totalFiles })}
      </p>
    </div>
  );

  const body = (
    <div className="space-y-3">
      <p className="text-sm">
        {t('bulkDownload.dialog.totalSize', { size: formatBytes(manifest.totalBytes) })}
      </p>

      {isLarge ? (
        <div
          data-testid="bulk-download-large-size-warning"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('bulkDownload.dialog.largeSizeWarning')}</span>
        </div>
      ) : null}

      {hasSkipped ? (
        <div
          data-testid="bulk-download-skipped-units-warning"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t('bulkDownload.dialog.skippedUnitsWarning', {
              skipped: skippedCount,
              total: totalSelectedUnits,
            })}
          </span>
        </div>
      ) : null}
    </div>
  );

  const footer = (
    <>
      <Button variant="outline" onClick={onCancel} data-testid="bulk-download-cancel">
        {t('bulkDownload.dialog.cancelBtn')}
      </Button>
      <Button onClick={onConfirm} data-testid="bulk-download-confirm">
        {t('bulkDownload.dialog.confirmBtn')}
      </Button>
    </>
  );

  return (
    <ModalDialog
      header={header}
      footer={footer}
      onClose={onCancel}
      testId="bulk-download-confirm-dialog"
      maxWidth={DIALOG_MAX_WIDTH}
    >
      {body}
    </ModalDialog>
  );
}
